<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Radio Selection Card Component
      p.text--secondary Individual radio buttons wrapped in card containers, designed to work with YzRadioGroup for enhanced visual selection.

    // Basic Usage
    .component-section.mb-8#basic-usage
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-usage')") Basic Usage
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('basic-usage')") Basic Radio Selection Cards
          p.text--secondary.caption Individual radio options wrapped in cards for better visual hierarchy

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
          .mb-3
            strong Selected: {{ JSON.stringify(basicValue) }}
          yz-radio-group(v-model="basicValue")
            .d-flex.flex-column.gap-3
              yz-radio-selection-card(value="option1" label="Option 1" description="First choice with basic functionality")
              yz-radio-selection-card(value="option2" label="Option 2" description="Second choice with additional features")
              yz-radio-selection-card(value="option3" label="Option 3" description="Third choice with premium features")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === basicCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === basicCode ? 'success' : ''" @click="copyCode(basicCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ basicCode }}

    // Card Customization
    .component-section.mb-8#card-customization
      h3.mb-4.cursor-pointer(@click="navigateToSection('card-customization')") Card Customization
      .variations-grid
        .variation-item.mb-6(v-for="variation in cardVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
            .mb-3
              strong Selected: {{ JSON.stringify($data[variation.model]) }}
            yz-radio-group(v-model="$data[variation.model]")
              .d-flex.flex-column.gap-3
                yz-radio-selection-card(value="a" label="Option A" description="First option" v-bind="variation.props")
                yz-radio-selection-card(value="b" label="Option B" description="Second option" v-bind="variation.props")
                yz-radio-selection-card(value="c" label="Option C" description="Third option" v-bind="variation.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Layout Variations
    .component-section.mb-8#layout-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('layout-variations')") Layout Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in layoutVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-3
              strong Selected: {{ JSON.stringify($data[variation.model]) }}
            yz-radio-group(v-model="$data[variation.model]" v-bind="variation.groupProps")
              div(:class="variation.containerClass")
                yz-radio-selection-card(value="horizontal-a" label="Option A" description="First choice")
                yz-radio-selection-card(value="horizontal-b" label="Option B" description="Second choice")
                yz-radio-selection-card(value="horizontal-c" label="Option C" description="Third choice")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Custom Slots
    .component-section.mb-8#custom-slots
      h3.mb-4.cursor-pointer(@click="navigateToSection('custom-slots')") Custom Slots
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('custom-slots')") Label and Description Slots
          p.text--secondary.caption Using custom content for labels and descriptions

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(slotsValue) }}
          yz-radio-group(v-model="slotsValue")
            .d-flex.flex-column.gap-3
              yz-radio-selection-card(value="basic")
                template(#label)
                  .d-flex.align-center
                    v-icon.mr-2(color="success" small) mdi-star
                    span.font-weight-medium Basic Plan
                template(#description)
                  .d-flex.align-center.justify-space-between
                    span $9/month - Essential features
                    yz-badge(color="green-100" text-color="green-500" size="sm") Popular
              
              yz-radio-selection-card(value="pro")
                template(#label)
                  .d-flex.align-center
                    v-icon.mr-2(color="primary" small) mdi-crown
                    span.font-weight-medium Pro Plan
                template(#description)
                  span $19/month - Advanced features with priority support
              
              yz-radio-selection-card(value="enterprise")
                template(#label)
                  .d-flex.align-center
                    v-icon.mr-2(color="warning" small) mdi-diamond-stone
                    span.font-weight-medium Enterprise Plan
                template(#description)
                  span $49/month - Full feature access with dedicated support

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === slotsCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === slotsCode ? 'success' : ''" @click="copyCode(slotsCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ slotsCode }}

    // Alignment Variations
    .component-section.mb-8#alignment-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('alignment-variations')") Alignment Variations
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('alignment-variations')") Content Alignment Options
          p.text--secondary.caption Different alignment options for radio and content

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
          .mb-3
            strong Selected: {{ JSON.stringify(alignmentValue) }}
          yz-radio-group(v-model="alignmentValue")
            .d-flex.flex-column.gap-3
              yz-radio-selection-card(value="start" label="Start Alignment (default)" description="Radio and content aligned to start/top" align="start")
              yz-radio-selection-card(value="center" label="Center Alignment" description="Radio and content vertically centered" align="center")
              yz-radio-selection-card(value="end" label="End Alignment" description="Radio and content aligned to end/bottom" align="end")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === alignmentCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === alignmentCode ? 'success' : ''" @click="copyCode(alignmentCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ alignmentCode }}

    // v-form Validation Examples
    .component-section.mb-8#validation-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('validation-examples')") v-form Validation Examples
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('validation-examples')") Form Validation Integration
          p.text--secondary.caption Radio selection cards with comprehensive v-form validation using rules array and required props

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          .mb-4
            h5.mb-2 Required validation
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(rulesRadioValue) }}
            yz-radio-group(ref="rulesRadioGroup" v-model="rulesRadioValue" required requiredErrorMessage="Please select a plan option")
              .d-flex.flex-column.gap-3
                yz-radio-selection-card(value="basic" label="Basic Plan" description="Essential features for getting started")
                yz-radio-selection-card(value="pro" label="Pro Plan" description="Advanced features with priority support")
            .mt-4
              .d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetRulesRadioGroup")
                yz-btn(label="Validate" @click="validateRulesRadioGroup" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="rulesValidationResult" :class="{ 'success--text': rulesValidationResult === 'Valid', 'error--text': rulesValidationResult === 'Invalid' }") {{ rulesValidationResult }}
          
          .mb-4
            h5.mb-2 Combined required + custom validation rules
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(rulesRadioValue2) }}
            yz-radio-group(ref="customRulesRadioGroup" v-model="rulesRadioValue2" required requiredErrorMessage="Please select a plan option" :rules="[v => v === 'pro' || 'Please select the Pro Plan for this feature']")
              .d-flex.flex-column.gap-3
                yz-radio-selection-card(value="basic" label="Basic Plan" description="Essential features for getting started")
                yz-radio-selection-card(value="pro" label="Pro Plan" description="Advanced features with priority support")
            .mt-4
              .d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetCustomRulesRadioGroup")
                yz-btn(label="Validate" @click="validateCustomRulesRadioGroup" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="customRulesValidationResult" :class="{ 'success--text': customRulesValidationResult === 'Valid', 'error--text': customRulesValidationResult === 'Invalid' }") {{ customRulesValidationResult }}
          
          .mb-4
            h5.mb-2 Required validation using only rules (no required prop)
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(rulesRadioValue3) }}
            yz-radio-group(ref="rulesOnlyRadioGroup" v-model="rulesRadioValue3" :rules="[v => !!v || 'This field is required']")
              .d-flex.flex-column.gap-3
                yz-radio-selection-card(value="basic" label="Basic Plan" description="Essential features for getting started")
                yz-radio-selection-card(value="pro" label="Pro Plan" description="Advanced features with priority support")
            .mt-4
              .d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetRulesOnlyRadioGroup")
                yz-btn(label="Validate" @click="validateRulesOnlyRadioGroup" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="rulesOnlyValidationResult" :class="{ 'success--text': rulesOnlyValidationResult === 'Valid', 'error--text': rulesOnlyValidationResult === 'Invalid' }") {{ rulesOnlyValidationResult }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            span.font-weight-medium Code Examples
          
          .code-examples
            .mb-3
              .d-flex.align-center.mb-1
                yz-btn(icon-only size="md" :icon="copiedCode === requiredCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === requiredCode ? 'success' : ''" @click="copyCode(requiredCode)")
                span.font-weight-medium.ml-2 Required validation
              pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ requiredCode }}
            
            .mb-3
              .d-flex.align-center.mb-1
                yz-btn(icon-only size="md" :icon="copiedCode === customRulesCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === customRulesCode ? 'success' : ''" @click="copyCode(customRulesCode)")
                span.font-weight-medium.ml-2 Combined validation rules
              pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ customRulesCode }}
            
            .mb-3
              .d-flex.align-center.mb-1
                yz-btn(icon-only size="md" :icon="copiedCode === rulesOnlyCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === rulesOnlyCode ? 'success' : ''" @click="copyCode(rulesOnlyCode)")
                span.font-weight-medium.ml-2 Rules-only validation
              pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ rulesOnlyCode }}

    // Optional Validation Examples
    .component-section.mb-8#optional-validation-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('optional-validation-examples')") Optional Validation Examples
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('optional-validation-examples')") Optional Plan Selection
          p.text--secondary.caption Optional fields that validate only when a selection is made - useful for subscription plans, service tiers, and optional upgrades
        
        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          
          // Optional subscription plan selection
          .mb-6
            h5.mb-2 Optional subscription plan
            small.text--secondary Selected: {{ JSON.stringify(optionalPlanValue) }}
            v-form.mt-2(ref="optionalPlanForm")
              yz-radio-group(v-model="optionalPlanValue" :rules="optionalPlanRules")
                .d-flex.flex-column.gap-3
                  yz-radio-selection-card(value="basic" label="Basic Plan" description="$9/month - Essential features for small teams")
                  yz-radio-selection-card(value="pro" label="Pro Plan" description="$19/month - Advanced features with analytics")
                  yz-radio-selection-card(value="enterprise" label="Enterprise Plan" description="$49/month - Full access with dedicated support")
            
            .d-flex.gap-2.mt-3
              yz-btn(size="sm" color="primary" @click="validateOptionalPlanForm") Validate
              yz-btn(size="sm" outlined @click="resetOptionalPlanForm") Reset
              span.yz-regular-body-xs.ml-2(v-if="optionalPlanValidationResult" :class="{ 'success--text': optionalPlanValidationResult === 'Valid', 'error--text': optionalPlanValidationResult === 'Invalid' }") {{ optionalPlanValidationResult }}
          
          // Optional addon selection
          .mb-6
            h5.mb-2 Optional addon services
            small.text--secondary Selected: {{ JSON.stringify(optionalAddonValue) }}
            v-form.mt-2(ref="optionalAddonForm")
              yz-radio-group(v-model="optionalAddonValue" :rules="optionalAddonRules")
                .d-flex.flex-column.gap-3
                  yz-radio-selection-card(value="backup" label="Cloud Backup" description="Automated daily backups to cloud storage")
                  yz-radio-selection-card(value="analytics" label="Advanced Analytics" description="Detailed insights and reporting dashboard")
                  yz-radio-selection-card(value="support" label="Priority Support" description="24/7 dedicated customer support channel")
            
            .d-flex.gap-2.mt-3
              yz-btn(size="sm" color="primary" @click="validateOptionalAddonForm") Validate
              yz-btn(size="sm" outlined @click="resetOptionalAddonForm") Reset
              span.yz-regular-body-xs.ml-2(v-if="optionalAddonValidationResult" :class="{ 'success--text': optionalAddonValidationResult === 'Valid', 'error--text': optionalAddonValidationResult === 'Invalid' }") {{ optionalAddonValidationResult }}
          
          // Optional delivery method
          .mb-6
            h5.mb-2 Optional delivery preferences
            small.text--secondary Selected: {{ JSON.stringify(optionalDeliveryValue) }}
            v-form.mt-2(ref="optionalDeliveryForm")
              yz-radio-group(v-model="optionalDeliveryValue" :rules="optionalDeliveryRules")
                .d-flex.flex-column.gap-3
                  yz-radio-selection-card(value="standard" label="Standard Delivery" description="5-7 business days via regular shipping")
                  yz-radio-selection-card(value="express" label="Express Delivery" description="2-3 business days with tracking")
                  yz-radio-selection-card(value="overnight" label="Overnight Delivery" description="Next business day delivery available")
            
            .d-flex.gap-2.mt-3
              yz-btn(size="sm" color="primary" @click="validateOptionalDeliveryForm") Validate
              yz-btn(size="sm" outlined @click="resetOptionalDeliveryForm") Reset
              span.yz-regular-body-xs.ml-2(v-if="optionalDeliveryValidationResult" :class="{ 'success--text': optionalDeliveryValidationResult === 'Valid', 'error--text': optionalDeliveryValidationResult === 'Invalid' }") {{ optionalDeliveryValidationResult }}
        
        .variation-code
          .code-header.d-flex.align-center.mb-2
            span.font-weight-medium Code Examples
          
          // Optional plan selection code
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalPlanCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalPlanCode ? 'success' : ''" @click="copyCode(optionalPlanCode)")
              span.font-weight-medium.ml-2 Optional plan selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalPlanCode }}
          
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalAddonCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalAddonCode ? 'success' : ''" @click="copyCode(optionalAddonCode)")
              span.font-weight-medium.ml-2 Optional addon selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalAddonCode }}
          
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalDeliveryCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalDeliveryCode ? 'success' : ''" @click="copyCode(optionalDeliveryCode)")
              span.font-weight-medium.ml-2 Optional delivery selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalDeliveryCode }}

    // Smart Padding
    .component-section.mb-8#smart-padding
      h3.mb-4.cursor-pointer(@click="navigateToSection('smart-padding')") Smart Padding
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('smart-padding')") Automatic Padding Detection
          p.text--secondary.caption Component automatically detects parent padding classes and applies them intelligently

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          .mb-3
            strong Selected: {{ JSON.stringify(paddingValue) }}
          yz-radio-group(v-model="paddingValue")
            .d-flex.flex-column.gap-3
              yz-radio-selection-card.pa-2(value="small" label="Small Padding (pa-2)" description="Custom padding applied via class")
              yz-radio-selection-card.pa-6(value="large" label="Large Padding (pa-6)" description="Custom padding applied via class")
              yz-radio-selection-card.px-8.py-2(value="custom" label="Custom Padding (px-8 py-2)" description="Custom horizontal and vertical padding")
              yz-radio-selection-card(value="default" label="Default Padding (pa-4)" description="Default padding when no parent classes detected")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === paddingCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === paddingCode ? 'success' : ''" @click="copyCode(paddingCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ paddingCode }}


    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      // Props
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // Events
      h4.mb-3 Events
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Description
                th Parameters
            tbody
              tr(v-for="event in componentEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.single-line {{ event.description }}
                td.text--secondary.text-no-wrap {{ event.parameters }}

      // Slots
      h4.mb-3 Slots
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot Name
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}
</template>

<script>
export default {
  name: "RadioSelectionCard",
  data() {
    return {
      copiedCode: null,
      basicValue: 'option1',
      borderValue: 'a',
      colorValue: 'a',
      radiusValue: 'a',
      disabledValue: 'a',
      horizontalValue: 'horizontal-a',
      spacingValue: 'horizontal-a',
      slotsValue: 'pro',
      alignmentValue: 'start',
      paddingValue: 'default',
      invalidValue: null,
      rulesRadioValue: null,
      rulesRadioValue2: null,
      rulesRadioValue3: null,
      rulesValidationResult: null,
      customRulesValidationResult: null,
      rulesOnlyValidationResult: null,
      
      // Optional Validation Examples Data
      optionalPlanValue: null,
      optionalAddonValue: null,
      optionalDeliveryValue: null,
      optionalPlanValidationResult: null,
      optionalAddonValidationResult: null,
      optionalDeliveryValidationResult: null,
      basicCode: `yz-radio-group(v-model="selectedValue")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="option1" label="Option 1" description="First choice with basic functionality")
    yz-radio-selection-card(value="option2" label="Option 2" description="Second choice with additional features")
    yz-radio-selection-card(value="option3" label="Option 3" description="Third choice with premium features")`,
      slotsCode: `yz-radio-group(v-model="selectedValue")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="basic")
      template(#label)
        .d-flex.align-center
          v-icon.mr-2(color="success" small) mdi-star
          span.font-weight-medium Basic Plan
      template(#description)
        .d-flex.align-center.justify-space-between
          span $9/month - Essential features
          yz-badge(color="green-100" text-color="green-500" size="sm") Popular`,
      alignmentCode: `yz-radio-group(v-model="selectedValue")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="start" label="Start Alignment (default)" description="Radio and content aligned to start/top" align="start")
    yz-radio-selection-card(value="center" label="Center Alignment" description="Radio and content vertically centered" align="center")
    yz-radio-selection-card(value="end" label="End Alignment" description="Radio and content aligned to end/bottom" align="end")`,
      paddingCode: `yz-radio-group(v-model="selectedValue")
  .d-flex.flex-column.gap-3
    // Custom small padding
    yz-radio-selection-card.pa-2(value="small" label="Small Padding (pa-2)" description="Custom padding applied via class")
    
    // Custom large padding
    yz-radio-selection-card.pa-6(value="large" label="Large Padding (pa-6)" description="Custom padding applied via class")
    
    // Custom directional padding
    yz-radio-selection-card.px-8.py-2(value="custom" label="Custom Padding (px-8 py-2)" description="Custom horizontal and vertical padding")
    
    // Default padding (pa-4) when no parent classes
    yz-radio-selection-card(value="default" label="Default Padding (pa-4)" description="Default padding when no parent classes detected")`,
      requiredCode: `yz-radio-group(v-model="selectedValue" required required-error-message="Please select a plan option")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="basic" label="Basic Plan" description="Essential features for getting started")
    yz-radio-selection-card(value="pro" label="Pro Plan" description="Advanced features with priority support")`,
      customRulesCode: `yz-radio-group(v-model="selectedValue" required required-error-message="Please select a plan option" :rules="[v => v === 'pro' || 'Please select the Pro Plan for this feature']")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="basic" label="Basic Plan" description="Essential features for getting started")
    yz-radio-selection-card(value="pro" label="Pro Plan" description="Advanced features with priority support")`,
      rulesOnlyCode: `yz-radio-group(v-model="selectedValue" :rules="[v => !!v || 'This field is required']")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="basic" label="Basic Plan" description="Essential features for getting started")
    yz-radio-selection-card(value="pro" label="Pro Plan" description="Advanced features with priority support")`,
      cardVariations: [
        {
          name: 'Custom Border',
          id: 'custom-border',
          description: 'Cards with custom border and background colors',
          model: 'borderValue',
          props: {
            borderColor: 'yz-blue-500',
            color: 'yz-blue-100'
          },
          code: `yz-radio-group(v-model="selectedValue")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="a" label="Option A" description="First option" border-color="yz-blue-500" color="yz-blue-100")
    yz-radio-selection-card(value="b" label="Option B" description="Second option" border-color="yz-blue-500" color="yz-blue-100")
    yz-radio-selection-card(value="c" label="Option C" description="Third option" border-color="yz-blue-500" color="yz-blue-100")`
        },
        {
          name: 'Custom Background',
          id: 'custom-background',
          description: 'Cards with custom background color',
          model: 'colorValue',
          props: {
            color: 'blue-100',
            selectedColor: 'neutral-400'
          },
          code: `yz-radio-group(v-model="selectedValue")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="a" label="Option A" description="First option" color="blue-100" selected-color="neutral-400")
    yz-radio-selection-card(value="b" label="Option B" description="Second option" color="blue-100" selected-color="neutral-400")
    yz-radio-selection-card(value="c" label="Option C" description="Third option" color="blue-100" selected-color="neutral-400")`
        },
        {
          name: 'Custom Radius',
          id: 'custom-radius',
          description: 'Cards with custom border radius',
          model: 'radiusValue',
          props: {
            radius: '20px'
          },
          code: `yz-radio-group(v-model="selectedValue")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="a" label="Option A" description="First option" radius="20px")
    yz-radio-selection-card(value="b" label="Option B" description="Second option" radius="20px")
    yz-radio-selection-card(value="c" label="Option C" description="Third option" radius="20px")`
        },
        {
          name: 'Disabled State',
          id: 'disabled-state',
          description: 'Disabled radio selection cards',
          model: 'disabledValue',
          props: {
            disabled: true
          },
          code: `yz-radio-group(v-model="selectedValue")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="a" label="Option A" description="First option" disabled)
    yz-radio-selection-card(value="b" label="Option B" description="Second option" disabled)
    yz-radio-selection-card(value="c" label="Option C" description="Third option" disabled)`
        }
      ],
      layoutVariations: [
        {
          name: 'Horizontal Layout',
          id: 'horizontal-layout',
          description: 'Radio cards arranged horizontally',
          model: 'horizontalValue',
          groupProps: {
            direction: 'horizontal'
          },
          containerClass: 'd-flex gap-3',
          code: `yz-radio-group(v-model="selectedValue" direction="horizontal")
  .d-flex.gap-3
    yz-radio-selection-card(value="horizontal-a" label="Option A" description="First choice")
    yz-radio-selection-card(value="horizontal-b" label="Option B" description="Second choice")
    yz-radio-selection-card(value="horizontal-c" label="Option C" description="Third choice")`
        },
        {
          name: 'Custom Spacing',
          id: 'custom-spacing',
          description: 'Radio cards with custom spacing',
          model: 'spacingValue',
          groupProps: {
            direction: 'horizontal',
            spacing: '32px'
          },
          containerClass: 'd-flex gap-8',
          code: `yz-radio-group(v-model="selectedValue" direction="horizontal" spacing="32px")
  .d-flex.gap-8
    yz-radio-selection-card(value="horizontal-a" label="Option A" description="First choice")
    yz-radio-selection-card(value="horizontal-b" label="Option B" description="Second choice")
    yz-radio-selection-card(value="horizontal-c" label="Option C" description="Third choice")`
        }
      ],
      componentProps: [
        {
          name: 'value',
          type: 'Any',
          default: '-',
          description: 'The value of this radio option (required)'
        },
        {
          name: 'label',
          type: 'String',
          default: '',
          description: 'Label text for the radio card'
        },
        {
          name: 'description',
          type: 'String',
          default: '',
          description: 'Description text displayed below the label'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the radio card is disabled'
        },
        {
          name: 'align',
          type: 'String',
          default: 'start',
          description: 'Vertical alignment of radio and content: "start", "center", or "end"'
        },
        {
          name: 'radius',
          type: 'String|Number',
          default: '10px',
          description: 'Border radius of the card'
        },
        {
          name: 'color',
          type: 'String',
          default: 'white',
          description: 'Background color of the card when not selected'
        },
        {
          name: 'selectedColor',
          type: 'String',
          default: 'neutral-200',
          description: 'Background color of the card when selected'
        },
        {
          name: 'borderColor',
          type: 'String',
          default: 'neutral-300',
          description: 'Border color of the card when not selected'
        },
        {
          name: 'selectedBorderColor',
          type: 'String',
          default: 'neutral-700',
          description: 'Border color of the card when selected'
        },
        {
          name: 'borderWidth',
          type: 'String|Number',
          default: '1',
          description: 'Border width of the card when not selected'
        },
        {
          name: 'selectedBorderWidth',
          type: 'String|Number',
          default: '2',
          description: 'Border width of the card when selected'
        },
        {
          name: 'borderStyle',
          type: 'String',
          default: 'solid',
          description: 'Border style of the card: "solid", "dashed", or "dotted"'
        },
        {
          name: 'hoverBackgroundColor',
          type: 'String',
          default: 'neutral-200',
          description: 'Background color of the card on hover. Accepts hex colors (#ff0000), CSS variables (var(--color)), or design system tokens (primary-500)'
        },
        {
          name: 'hoverBorderColor',
          type: 'String',
          default: 'neutral-500',
          description: 'Border color of the card on hover. Accepts hex colors (#ff0000), CSS variables (var(--color)), or design system tokens (primary-500)'
        },
        {
          name: 'invalid',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the radio card should show error state styling. Automatically inherits from parent radio group validation.'
        }
      ],
      componentEvents: [
        {
          name: 'input',
          description: 'Emitted when this radio card is selected (standalone use)',
          parameters: 'value: Any'
        },
        {
          name: 'change',
          description: 'Emitted when this radio card is selected',
          parameters: 'value: Any'
        },
        {
          name: 'focus',
          description: 'Emitted when the radio card receives focus',
          parameters: 'event: FocusEvent'
        },
        {
          name: 'blur',
          description: 'Emitted when the radio card loses focus',
          parameters: 'event: FocusEvent'
        }
      ],
      componentSlots: [
        {
          name: 'label',
          description: 'Custom content for the label area'
        },
        {
          name: 'description',
          description: 'Custom content for the description area'
        }
      ]
    }
  },
  computed: {
    // Optional Validation Rules
    optionalPlanRules() {
      return [
        v => !v || ['basic', 'pro', 'enterprise'].includes(v) || 'Please select a valid subscription plan'
      ]
    },
    optionalAddonRules() {
      return [
        v => !v || ['backup', 'analytics', 'support'].includes(v) || 'Please select a valid addon service'
      ]
    },
    optionalDeliveryRules() {
      return [
        v => !v || ['standard', 'express', 'overnight'].includes(v) || 'Please select a valid delivery method'
      ]
    },
    
    // Code examples for optional validation
    optionalPlanCode() {
      return `yz-radio-group(v-model="subscriptionPlan" :rules="optionalPlanRules")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="basic" label="Basic Plan" description="$9/month - Essential features for small teams")
    yz-radio-selection-card(value="pro" label="Pro Plan" description="$19/month - Advanced features with analytics")
    yz-radio-selection-card(value="enterprise" label="Enterprise Plan" description="$49/month - Full access with dedicated support")

// Validation rule
optionalPlanRules() {
  return [
    v => !v || ['basic', 'pro', 'enterprise'].includes(v) || 'Please select a valid subscription plan'
  ]
}`
    },
    optionalAddonCode() {
      return `yz-radio-group(v-model="addonService" :rules="optionalAddonRules")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="backup" label="Cloud Backup" description="Automated daily backups to cloud storage")
    yz-radio-selection-card(value="analytics" label="Advanced Analytics" description="Detailed insights and reporting dashboard")
    yz-radio-selection-card(value="support" label="Priority Support" description="24/7 dedicated customer support channel")

// Validation rule
optionalAddonRules() {
  return [
    v => !v || ['backup', 'analytics', 'support'].includes(v) || 'Please select a valid addon service'
  ]
}`
    },
    optionalDeliveryCode() {
      return `yz-radio-group(v-model="deliveryMethod" :rules="optionalDeliveryRules")
  .d-flex.flex-column.gap-3
    yz-radio-selection-card(value="standard" label="Standard Delivery" description="5-7 business days via regular shipping")
    yz-radio-selection-card(value="express" label="Express Delivery" description="2-3 business days with tracking")
    yz-radio-selection-card(value="overnight" label="Overnight Delivery" description="Next business day delivery available")

// Validation rule
optionalDeliveryRules() {
  return [
    v => !v || ['standard', 'express', 'overnight'].includes(v) || 'Please select a valid delivery method'
  ]
}`
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    validateRulesRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.rulesRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.validate === 'function') {
          const isValid = radioGroupRef.validate(true);
          this.rulesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.rulesValidationResult = null;
          }, 1500);
        }
      });
    },
    validateCustomRulesRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.customRulesRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.validate === 'function') {
          const isValid = radioGroupRef.validate(true);
          this.customRulesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.customRulesValidationResult = null;
          }, 1500);
        }
      });
    },
    validateRulesOnlyRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.rulesOnlyRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.validate === 'function') {
          const isValid = radioGroupRef.validate(true);
          this.rulesOnlyValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.rulesOnlyValidationResult = null;
          }, 1500);
        }
      });
    },
    resetRulesRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.rulesRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.resetValidation === 'function') {
          radioGroupRef.resetValidation();
          this.rulesValidationResult = null;
          this.rulesRadioValue = null;
        }
      });
    },
    resetCustomRulesRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.customRulesRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.resetValidation === 'function') {
          radioGroupRef.resetValidation();
          this.customRulesValidationResult = null;
          this.rulesRadioValue2 = null;
        }
      });
    },
    resetRulesOnlyRadioGroup() {
      this.$nextTick(() => {
        const radioGroupRefArray = this.$refs.rulesOnlyRadioGroup;
        const radioGroupRef = Array.isArray(radioGroupRefArray) ? radioGroupRefArray[0] : radioGroupRefArray;
        if (radioGroupRef && typeof radioGroupRef.resetValidation === 'function') {
          radioGroupRef.resetValidation();
          this.rulesOnlyValidationResult = null;
          this.rulesRadioValue3 = null;
        }
      });
    },
    
    // Optional Validation Methods
    validateOptionalPlanForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalPlanForm;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalPlanValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalPlanValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalPlanForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalPlanForm;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalPlanValidationResult = null;
          this.optionalPlanValue = null;
        }
      });
    },
    validateOptionalAddonForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalAddonForm;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalAddonValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalAddonValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalAddonForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalAddonForm;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalAddonValidationResult = null;
          this.optionalAddonValue = null;
        }
      });
    },
    validateOptionalDeliveryForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalDeliveryForm;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalDeliveryValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalDeliveryValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalDeliveryForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalDeliveryForm;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalDeliveryValidationResult = null;
          this.optionalDeliveryValue = null;
        }
      });
    },
    
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: transparent;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>